import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useMemo, useState } from "react";
import { Edit2, Trash2, X, Search, CheckCheck, MessageCircle } from "lucide-react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import { useNavigate } from "react-router-dom";
import "./BlogAdminTheme.css";
import "./AdminBlogComments.css";

const TABS = [
    { id: "Pending", label: "Pending review" },
    { id: "Active", label: "Approved" },
    { id: "All", label: "All" },
];

const AdminBlogComments = () => {
    const [comments, setComments] = useState([]);
    const [blogMap, setBlogMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState("Pending");
    const [selected, setSelected] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedComment, setSelectedComment] = useState(null);

    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const navigate = useNavigate();

    const fetchBlogs = async () => {
        try {
            const res = await fetch(`${url}/blog`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
                const map = {};
                data.data.forEach((b) => {
                    map[b.id] = b;
                });
                setBlogMap(map);
            }
        } catch {
            /* optional */
        }
    };

    const fetchComments = async () => {
        try {
            const res = await fetch(`${url}/blog-comment/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            const list = Array.isArray(data.data) ? data.data : [];
            setComments(list);
        } catch {
            toast.error("Failed to load comments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBlogs();
        fetchComments();
    }, []);

    const filtered = useMemo(() => {
        const s = search.toLowerCase();
        return comments.filter((c) => {
            const matchTab =
                activeTab === "All" ||
                (activeTab === "Pending" && c.status === "Inactive") ||
                (activeTab === "Active" && c.status === "Active");
            const matchSearch =
                !s ||
                c.author?.toLowerCase().includes(s) ||
                c.email?.toLowerCase().includes(s) ||
                c.message?.toLowerCase().includes(s) ||
                String(c.blog_id).includes(s) ||
                blogMap[c.blog_id]?.title?.toLowerCase().includes(s);
            return matchTab && matchSearch;
        });
    }, [comments, search, activeTab, blogMap]);

    const pendingCount = comments.filter((c) => c.status === "Inactive").length;

    const toggleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (filtered.every((c) => selected.includes(c.id))) {
            setSelected((prev) => prev.filter((id) => !filtered.some((c) => c.id === id)));
        } else {
            const ids = filtered.map((c) => c.id);
            setSelected((prev) => [...new Set([...prev, ...ids])]);
        }
    };

    const setCommentStatus = async (commentId, status) => {
        const res = await fetch(`${url}/blog-comment/status/${commentId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status }),
        });
        if (!res.ok) throw new Error("status failed");
        setComments((prev) =>
            prev.map((c) => (c.id === commentId ? { ...c, status } : c))
        );
    };

    const bulkCommentAction = async (action) => {
        if (!selected.length) return null;
        const res = await fetch(`${url}/blog-comment/bulk`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ids: selected, action }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "bulk failed");
        return data;
    };

    const notifyBulkResult = (data, label) => {
        const count = data.count ?? 0;
        if (count > 0) {
            toast.success(`${label} ${count} comment(s)`);
        } else {
            toast.info("No comments were updated — they may belong to another account.");
        }
        if (data.skippedCount > 0) {
            toast.warn(`${data.skippedCount} selected comment(s) were skipped (not found for your account).`);
        }
    };

    const matchedSet = (data) => new Set((data?.matchedIds || []).map((id) => Number(id)));

    const handleBulkApprove = async () => {
        if (!selected.length) return;
        try {
            const data = await bulkCommentAction("approve");
            const matched = matchedSet(data);
            setComments((prev) =>
                prev.map((c) =>
                    matched.has(Number(c.id)) ? { ...c, status: "Active" } : c
                )
            );
            notifyBulkResult(data, "Approved");
            setSelected((prev) => prev.filter((id) => !matched.has(Number(id))));
        } catch {
            toast.error("Bulk approve failed");
        }
    };

    const handleBulkReject = async () => {
        if (!selected.length) return;
        try {
            const data = await bulkCommentAction("reject");
            const matched = matchedSet(data);
            setComments((prev) =>
                prev.map((c) =>
                    matched.has(Number(c.id)) ? { ...c, status: "Inactive" } : c
                )
            );
            notifyBulkResult(data, "Set pending on");
            setSelected((prev) => prev.filter((id) => !matched.has(Number(id))));
        } catch {
            toast.error("Bulk update failed");
        }
    };

    const handleBulkDelete = async () => {
        if (!selected.length) return;
        if (!window.confirm(`Delete ${selected.length} comment(s) permanently?`)) return;
        try {
            const data = await bulkCommentAction("delete");
            const matched = matchedSet(data);
            setComments((prev) => prev.filter((c) => !matched.has(Number(c.id))));
            notifyBulkResult(data, "Deleted");
            setSelected((prev) => prev.filter((id) => !matched.has(Number(id))));
        } catch {
            toast.error("Bulk delete failed");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this comment permanently?")) return;
        try {
            const res = await fetch(`${url}/blog-comment/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error();
            toast.success("Comment deleted");
            setComments((prev) => prev.filter((c) => c.id !== id));
            setSelected((prev) => prev.filter((x) => x !== id));
        } catch {
            toast.error("Delete failed");
        }
    };

    const openEditModal = (comment) => {
        setSelectedComment({ ...comment });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedComment(null);
    };

    const handleUpdate = async () => {
        try {
            const res = await fetch(`${url}/blog-comment/${selectedComment.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    author: selectedComment.author,
                    email: selectedComment.email,
                    blog_id: selectedComment.blog_id,
                    message: selectedComment.message,
                    status: selectedComment.status,
                }),
            });
            if (!res.ok) throw new Error();
            toast.success("Comment updated");
            closeModal();
            fetchComments();
        } catch {
            toast.error("Update failed");
        }
    };

    if (loading) {
        return <div className="comment-queue-loading">Loading comments…</div>;
    }

    return (
        <div className="comment-queue">
            <header className="comment-queue-header">
                <div>
                    <h1 className="comment-queue-title">
                        <MessageCircle size={22} />
                        Comment moderation
                    </h1>
                    <p className="comment-queue-sub">
                        {pendingCount} pending · {comments.length} total
                    </p>
                </div>
            </header>

            <div className="comment-queue-toolbar">
                <div className="comment-queue-tabs">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={`comment-queue-tab ${activeTab === tab.id ? "is-active" : ""}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                            {tab.id === "Pending" && pendingCount > 0 && (
                                <span className="comment-queue-badge">{pendingCount}</span>
                            )}
                        </button>
                    ))}
                </div>
                <div className="comment-queue-search">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search comments…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {selected.length > 0 && (
                <div className="comment-queue-bulk">
                    <span>{selected.length} selected</span>
                    <button type="button" onClick={handleBulkApprove} className="comment-queue-bulk-btn comment-queue-bulk-btn--approve">
                        <CheckCheck size={16} />
                        Approve
                    </button>
                    <button type="button" onClick={handleBulkReject} className="comment-queue-bulk-btn">
                        Mark pending
                    </button>
                    <button type="button" onClick={handleBulkDelete} className="comment-queue-bulk-btn comment-queue-bulk-btn--delete">
                        <Trash2 size={16} />
                        Delete
                    </button>
                </div>
            )}

            <div className="comment-queue-table-wrap">
                <table className="comment-queue-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    checked={filtered.length > 0 && filtered.every((c) => selected.includes(c.id))}
                                    onChange={toggleSelectAll}
                                    aria-label="Select all"
                                />
                            </th>
                            <th>Author</th>
                            <th>Post</th>
                            <th>Comment</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="comment-queue-empty">No comments in this queue</td>
                            </tr>
                        ) : (
                            filtered.map((comment) => {
                                const post = blogMap[comment.blog_id];
                                return (
                                    <tr key={comment.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(comment.id)}
                                                onChange={() => toggleSelect(comment.id)}
                                            />
                                        </td>
                                        <td>
                                            <div className="comment-queue-author">{comment.author}</div>
                                            <div className="comment-queue-email">{comment.email}</div>
                                        </td>
                                        <td>
                                            {post ? (
                                                <button
                                                    type="button"
                                                    className="comment-queue-post-link"
                                                    onClick={() => navigate(`/edit-blog/${post.id}`)}
                                                >
                                                    {post.title}
                                                </button>
                                            ) : (
                                                <span>#{comment.blog_id}</span>
                                            )}
                                        </td>
                                        <td className="comment-queue-message">{comment.message}</td>
                                        <td className="comment-queue-date">
                                            {new Date(comment.createdAt).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <span
                                                className={`comment-queue-status comment-queue-status--${comment.status === "Active" ? "active" : "pending"}`}
                                            >
                                                {comment.status === "Active" ? "Approved" : "Pending"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="comment-queue-actions">
                                                {comment.status !== "Active" && (
                                                    <button
                                                        type="button"
                                                        title="Approve"
                                                        onClick={() => setCommentStatus(comment.id, "Active").then(() => toast.success("Approved"))}
                                                    >
                                                        <CheckCheck size={16} />
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => openEditModal(comment)} title="Edit">
                                                    <Edit2 size={16} />
                                                </button>
                                                <button type="button" onClick={() => handleDelete(comment.id)} title="Delete">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && selectedComment && (
                <div className="comment-queue-modal-backdrop">
                    <div className="comment-queue-modal">
                        <button type="button" className="comment-queue-modal-close" onClick={closeModal}>
                            <X size={20} />
                        </button>
                        <h3>Edit comment</h3>
                        <div className="comment-queue-modal-fields">
                            <input
                                type="text"
                                value={selectedComment.author}
                                onChange={(e) =>
                                    setSelectedComment({ ...selectedComment, author: e.target.value })
                                }
                                placeholder="Author"
                            />
                            <input
                                type="email"
                                value={selectedComment.email}
                                onChange={(e) =>
                                    setSelectedComment({ ...selectedComment, email: e.target.value })
                                }
                                placeholder="Email"
                            />
                            <select
                                value={selectedComment.status}
                                onChange={(e) =>
                                    setSelectedComment({ ...selectedComment, status: e.target.value })
                                }
                            >
                                <option value="Inactive">Pending</option>
                                <option value="Active">Approved</option>
                            </select>
                            <textarea
                                rows={4}
                                value={selectedComment.message}
                                onChange={(e) =>
                                    setSelectedComment({ ...selectedComment, message: e.target.value })
                                }
                            />
                        </div>
                        <div className="comment-queue-modal-actions">
                            <button type="button" onClick={closeModal}>Cancel</button>
                            <button type="button" className="comment-queue-save" onClick={handleUpdate}>
                                Save changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBlogComments;
